import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
    HttpError,
    IResourceComponentsProps,
    useNavigation,
    useTranslate,
} from '@refinedev/core';
import { Form, FormProps, Spin } from 'antd';
import { useForm } from '@refinedev/antd';
import { FormInstance } from 'antd/es/form';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';

import { TranslatedBreadcrumbs } from 'components/translated-breadcrumbs';
import { useNotification } from 'hooks/useNotification';
import { handleFormError } from 'utils/error-handlers';
import { InternalLayout } from 'layouts/InternalLayout';

import { useWizardForm } from '../../preliminary-application/create/hooks/useWizardForm';
import { StepNavigation } from 'components/preliminary-application/steps/StepNavigation';
import { Step1DrugInfo } from 'components/preliminary-application/steps/Step1DrugInfo';
import { Step2CertificateProcedure } from 'components/preliminary-application/steps/Step2CertificateProcedure';
import { Step3GeneralInfo } from 'components/preliminary-application/steps/Step3GeneralInfo';
import { Step4Documents } from 'components/preliminary-application/steps/Step4Documents';
import { Step5Submission } from 'components/preliminary-application/steps/Step5Submission';

import styles from './create.module.scss';
import { IFormValues } from '../../../interfaces/preliminary-application-wp.interface';

const storageNamespace = 'wp-wizard';
const storageDraftKey = `${storageNamespace}-draft`;
const storageFilesKey = `${storageNamespace}-files`;

export const PreliminaryApplicationOPCCreate: FC<
    IResourceComponentsProps
> = () => {
    const t = useTranslate();
    const { replace } = useNavigation();
    const { msgSuccess, msgError } = useNotification();

    const [fileList, setFileList] = useState<UploadFile[]>([]);

    // Store goToStep in a ref so it can be used in onMutationError callback
    const goToStepRef = useRef<((step: number) => void) | null>(null);

    const { formProps } = useForm<IFormValues, HttpError>({
        onMutationSuccess: () => {},
        onMutationError: (error: HttpError) => {
            handleFormError({
                e: error,
                form,
                setIsFormProcessing: () => {},
                setDisSubmit: () => {},
                msgError,
                t,
                navigateToStep: goToStepRef.current || undefined,
                redirectToErrorScreen: () => {
                    // Optional redirect if needed
                },
            });
        },
        redirect: false,
        resource: 'preliminary-request',
    });

    const form = formProps?.form as FormInstance;

    const {
        currentStep,
        isFormProcessing,
        disSubmit,
        goToStep,
        goNext,
        goPrevious,
        saveDraft,
        submitFinal,
    } = useWizardForm({
        form,
        fileList,
        setFileList,
        msgSuccess,
        msgError,
        t,
        replace,
        editRedirectBasePath: '/preliminary-applications-wp/edit',
        storageNamespace,
        requiredFieldsMap: {},
    });

    useEffect(() => {
        if (goToStep) {
            goToStepRef.current = goToStep;
        }
    }, [goToStep]);

    const onValuesChange: FormProps['onValuesChange'] = (
        changedValues: Record<string, any>
    ) => {
        if (!form) return;

        const fieldsToReset =
            Object.keys(changedValues ?? {}).map(fieldName => ({
                name: fieldName
                    .split('.')
                    .map(part => (Number.isInteger(+part) ? +part : part)),
                errors: [],
            })) ?? [];

        if (fieldsToReset.length) {
            form.setFields(fieldsToReset);
        }
    };

    const handleFileChange = useCallback(
        (newFileList: UploadFile[]) => {
            setFileList(newFileList);
            form?.setFieldsValue({ files: newFileList });

            const fileMetadata = newFileList.map(file => ({
                uid: file.uid,
                name: file.name,
                size: (file as any).size,
                type: (file as any).type,
                lastModifiedDate: (file as any).lastModifiedDate,
            }));
            localStorage.setItem(storageFilesKey, JSON.stringify(fileMetadata));
        },
        [form, storageFilesKey]
    );

    useEffect(() => {
        const savedDraft = localStorage.getItem(storageDraftKey);
        const savedFiles = localStorage.getItem(storageFilesKey);

        if (savedDraft && form) {
            try {
                const parsedData = JSON.parse(savedDraft);

                if (parsedData.reg_date) {
                    parsedData.reg_date = dayjs(parsedData.reg_date);
                }

                form.setFieldsValue(parsedData);

                if (savedFiles) {
                    const fileMetadata = JSON.parse(savedFiles);
                    const restoredFiles = fileMetadata.map((meta: any) => ({
                        uid: meta.uid,
                        name: meta.name,
                        size: meta.size,
                        type: meta.type,
                        lastModifiedDate: meta.lastModifiedDate,
                        status: 'done',
                    }));
                    setFileList(restoredFiles);
                    form.setFieldsValue({ files: restoredFiles });
                }
            } catch (error) {
                console.error(
                    'Failed to restore form data from localStorage:',
                    error
                );
            }
        }
    }, [form, storageDraftKey, storageFilesKey]);

    const formPropsForCreate: FormProps = {
        ...formProps,
        initialValues: {
            reg_date: dayjs(),
            is_electronic_form: false,
            is_mibp: false,
            is_ectd: false,
            medication_name: '',
            release_form: '',
            international_name_id: null,
            certificate: '',
            applicant_id: null,
            procedure_id: null,
            procedure_desc: '',
            notification_email: '',
            procedure: '',
            notes: '',
            application_notes: '',
            files: [],
        },
        layout: 'vertical',
        onFinish: submitFinal,
        onValuesChange: onValuesChange,
        disabled: isFormProcessing,
        preserve: true,
    };

    const formId = 'preliminary-application-wp-create-form';

    return (
        <InternalLayout
            breadcrumbs={<TranslatedBreadcrumbs />}
            onBack={() => replace('/preliminary-applications-wp')}
            title={t(
                'preliminary-applications-wp.titles.create',
                undefined,
                'Create an application'
            )}
            contentClassName={styles.internalLayout}
            step={{
                current: currentStep + 1,
                total: 5,
                label: `Крок ${currentStep + 1} із 5`,
            }}
        >
            <Spin spinning={isFormProcessing}>
                <Form
                    {...formPropsForCreate}
                    id={formId}
                    className={styles.formContainer}
                >
                    {currentStep === 0 && (
                        <Step1DrugInfo showInternationalName={false} />
                    )}
                    {currentStep === 1 && (
                        <Step2CertificateProcedure
                            procedureFilterMode="onlyPrice"
                            autoSelectFirstProcedure
                            showProcedureFields={false}
                        />
                    )}
                    {currentStep === 2 && (
                            <Step3GeneralInfo
                                showApplicationNotes
                                applicationNotesLabel={t(
                                    'preliminary-applications-wp.fields.application_notes',
                                    'Примітки до заяви'
                                )}
                                showCheckboxes={false}
                            />
                    )}
                    {currentStep === 3 && (
                        <Step4Documents
                            fileList={fileList}
                            onFileChange={handleFileChange}
                            accept=".pdf"
                        />
                    )}
                    {currentStep === 4 && (
                        <Step5Submission
                            fileList={fileList}
                            showRegDateField
                            showInternationalName={false}
                            showProcedureInfo={false}
                            showCheckboxes={false}
                        />
                    )}

                    <StepNavigation
                        currentStep={currentStep}
                        totalSteps={5}
                        onPrevious={goPrevious}
                        onNext={goNext}
                        onSave={saveDraft}
                        isProcessing={isFormProcessing}
                        disSubmit={disSubmit}
                        showSubmitButton={false}
                    />
                </Form>
            </Spin>
        </InternalLayout>
    );
};
